import { useEffect, useRef, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { getVuelos, subirVuelo, borrarVuelo, editarVuelo, plantillaDeMosaicos, type Vuelo } from '../../services/flights';
import { useSensors } from '../../hooks/useSensors';
import { useSensorNames } from '../../context/SensorNamesContext';
import { markerLabel } from '../../utils/deviceMarker';
import { distanciaTotal, areaGeodesica, perimetro, formatearDistancia, formatearArea } from '../../utils/geo';
import { getMediciones, crearMedicion, renombrarMedicion, borrarMedicion, type Medicion } from '../../services/measurements';

/**
 * Módulo Mapas — ortofotos de dron con línea de tiempo.
 *
 * Lo que esta pantalla hace y una plataforma de imágenes no puede: poner
 * los sensores encima de la foto. Ellas tienen la imagen, nosotros la
 * telemetría; juntarlas es la razón de que este módulo exista en vez de
 * mandar al cliente a otro lado.
 *
 * Acepta cualquier GeoTIFF georreferenciado, venga del software que venga:
 * el módulo no está atado a ningún proveedor.
 */

function pesoLegible(bytes: number): string {
  if (bytes >= 1024 ** 3) return `${(bytes / 1024 ** 3).toFixed(1)} GB`;
  return `${Math.round(bytes / 1024 ** 2)} MB`;
}

function fechaLegible(iso: string, idioma: string): string {
  // Se construye con componentes sueltos y NO con `new Date(iso)`: una fecha
  // sin hora se interpreta como UTC, y en México eso la corre un día hacia
  // atrás — el vuelo del 17 aparecería como del 16.
  const [a, m, d] = iso.split('-').map(Number);
  return new Date(a ?? 2000, (m ?? 1) - 1, d ?? 1)
    .toLocaleDateString(idioma, { day: 'numeric', month: 'short', year: 'numeric' });
}

export default function MapsPage({ userRole }: { userRole: string }) {
  const { t, i18n } = useTranslation();
  const puedeSubir = userRole === 'super_admin' || userRole === 'tenant_admin';
  const { sensors } = useSensors();
  const { getName } = useSensorNames();

  const [vuelos, setVuelos] = useState<Vuelo[]>([]);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [seleccionado, setSeleccionado] = useState<string | null>(null);
  const [opacidad, setOpacidad] = useState(1);
  /** Edición en línea del nombre y la fecha de una imagen. */
  const [editando, setEditando] = useState<string | null>(null);
  const [borrNombre, setBorrNombre] = useState('');
  const [borrFecha, setBorrFecha] = useState('');
  const [errorEdicion, setErrorEdicion] = useState<string | null>(null);

  /**
   * Medición sobre el mapa.
   *
   * `puntos` es la medición en curso; `medidas` las ya terminadas. Se
   * separan porque la de en curso se redibuja en cada clic y las demás no
   * tienen por qué volver a dibujarse cada vez.
   */
  const [herramienta, setHerramienta] = useState<'ninguna' | 'distancia' | 'area'>('ninguna');
  const [errorMedicion, setErrorMedicion] = useState<string | null>(null);
  const [puntos, setPuntos] = useState<L.LatLng[]>([]);
  const [medidas, setMedidas] = useState<Medicion[]>([]);
  const [renombrando, setRenombrando] = useState<string | null>(null);
  const [borrNombreMedida, setBorrNombreMedida] = useState('');
  const capaMedidasRef = useRef<L.LayerGroup | null>(null);
  const capaEnCursoRef = useRef<L.LayerGroup | null>(null);
  const herramientaRef = useRef(herramienta);
  useEffect(() => { herramientaRef.current = herramienta; }, [herramienta]);
  const terminarRef = useRef<() => void>(() => {});
  const traducirRef = useRef(t);
  useEffect(() => { traducirRef.current = t; }, [t]);
  /** Espejo de `puntos` para los manejadores del mapa, que se registran una
   *  sola vez y si no se quedarían con la lista vacía del primer render. */
  const puntosRef = useRef<L.LatLng[]>([]);
  const capaPreviaRef = useRef<L.LayerGroup | null>(null);
  const pistaRef = useRef<L.Tooltip | null>(null);
  const [subiendo, setSubiendo] = useState<number | null>(null);
  const [errorSubida, setErrorSubida] = useState<string | null>(null);

  const contenedorRef = useRef<HTMLDivElement>(null);
  const mapaRef = useRef<L.Map | null>(null);
  const capaVueloRef = useRef<L.TileLayer | null>(null);
  const capaSensoresRef = useRef<L.LayerGroup | null>(null);
  const archivoRef = useRef<HTMLInputElement>(null);

  const cargar = useCallback(async () => {
    setCargando(true);
    setError(null);
    try {
      const lista = await getVuelos();
      setVuelos(lista);
      // Se abre en el más reciente que se pueda ver: es lo que alguien
      // quiere el 95% de las veces.
      const listo = lista.find(v => v.estado === 'listo');
      setSeleccionado(prev => prev && lista.some(v => v.id === prev) ? prev : listo?.id ?? null);
    } catch {
      setError(t('maps.loadError'));
    } finally {
      setCargando(false);
    }
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  // Las mediciones viven en el servidor, a nivel cliente: se ven en
  // cualquier imagen y siguen ahí la semana entrante.
  useEffect(() => {
    getMediciones().then(setMedidas).catch(() => setMedidas([]));
  }, []);

  /**
   * Mientras haya un vuelo procesándose, se vuelve a preguntar.
   *
   * La extracción de ~34,000 mosaicos corre en el servidor después de que la
   * subida terminó, así que el vuelo nace en 'procesando'. Sin esto habría
   * que refrescar la página a ciegas para saber si ya quedó.
   */
  useEffect(() => {
    if (!vuelos.some(v => v.estado === 'procesando')) return;
    const t = setInterval(cargar, 4000);
    return () => clearInterval(t);
  }, [vuelos, cargar]);

  // ── Mapa base ─────────────────────────────────────────────────────
  useEffect(() => {
    if (!contenedorRef.current || mapaRef.current) return;
    let mapa: L.Map;
    try {
      mapa = L.map(contenedorRef.current).setView([27.9691, -101.4074], 14);
    } catch (err) {
      console.warn('[Mapas] Leaflet no arrancó:', err);
      return;
    }
    L.tileLayer(
      'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',
      { attribution: 'ESRI', maxZoom: 21, maxNativeZoom: 19 },
    ).addTo(mapa);
    capaSensoresRef.current = L.layerGroup().addTo(mapa);
    capaMedidasRef.current = L.layerGroup().addTo(mapa);
    capaEnCursoRef.current = L.layerGroup().addTo(mapa);

    capaPreviaRef.current = L.layerGroup().addTo(mapa);

    /**
     * Qué tan cerca hay que hacer clic de un vértice para "agarrarlo",
     * en pixeles. Doce es lo que la mano acierta sin pelear, y sigue siendo
     * chico comparado con la separación normal entre puntos.
     */
    const RADIO_CIERRE = 12;

    const cercaDe = (e: L.LeafletMouseEvent, punto: L.LatLng) =>
      mapa.latLngToContainerPoint(punto).distanceTo(mapa.latLngToContainerPoint(e.latlng)) <= RADIO_CIERRE;

    /**
     * Un clic sobre el ÚLTIMO punto cierra la medición; sobre cualquier
     * otro lado, agrega un vértice.
     *
     * Antes se cerraba con doble clic y no funcionaba bien: Leaflet dispara
     * dos `click` antes del `dblclick`, así que además de cerrar metía dos
     * vértices basura encimados al final. Cerrar sobre el último punto es
     * lo que hacen las herramientas de mapa serias, y se puede anunciar en
     * pantalla — un doble clic hay que adivinarlo.
     */
    mapa.on('click', (e: L.LeafletMouseEvent) => {
      const tipo = herramientaRef.current;
      if (tipo === 'ninguna') return;
      const actuales = puntosRef.current;
      const minimo = tipo === 'area' ? 3 : 2;

      if (actuales.length >= minimo) {
        const ultimo = actuales[actuales.length - 1]!;
        const primero = actuales[0]!;
        // En un área, el primer punto también cierra: es el gesto natural
        // de "ya le di la vuelta al terreno".
        if (cercaDe(e, ultimo) || (tipo === 'area' && cercaDe(e, primero))) {
          terminarRef.current();
          return;
        }
      }
      setPuntos(prev => [...prev, e.latlng]);
    });

    /**
     * La liga elástica: el tramo que va del último punto al cursor.
     *
     * Se dibuja directo sobre la capa, sin pasar por el estado de React —
     * el mouse dispara decenas de eventos por segundo y volver a renderizar
     * la pantalla en cada uno pondría el mapa a tirones.
     */
    mapa.on('mousemove', (e: L.LeafletMouseEvent) => {
      const tipo = herramientaRef.current;
      const capa = capaPreviaRef.current;
      const pista = pistaRef.current;
      if (tipo === 'ninguna' || !capa) return;

      const actuales = puntosRef.current;
      capa.clearLayers();

      if (actuales.length > 0) {
        const ultimo = actuales[actuales.length - 1]!;
        L.polyline([ultimo, e.latlng], { color: '#f59e0b', weight: 2, dashArray: '4,5', opacity: 0.85 }).addTo(capa);
        // En un área se insinúa también el cierre contra el primer punto,
        // para que se vea la figura que va a quedar y no sólo el tramo.
        if (tipo === 'area' && actuales.length >= 2) {
          L.polyline([e.latlng, actuales[0]!], { color: '#f59e0b', weight: 1.5, dashArray: '3,6', opacity: 0.5 }).addTo(capa);
        }
      }

      if (pista) {
        const minimo = tipo === 'area' ? 3 : 2;
        const clave = actuales.length === 0 ? 'hintStart'
          : actuales.length < minimo ? 'hintContinue'
          : 'hintFinish';
        pista.setLatLng(e.latlng).setContent(traducirRef.current(`maps.${clave}`));
      }
    });

    mapaRef.current = mapa;
    return () => { mapa.remove(); mapaRef.current = null; };
  }, []);

  // ── Capa del vuelo seleccionado ───────────────────────────────────
  useEffect(() => {
    const mapa = mapaRef.current;
    if (!mapa) return;
    if (capaVueloRef.current) {
      mapa.removeLayer(capaVueloRef.current);
      capaVueloRef.current = null;
    }
    const vuelo = vuelos.find(v => v.id === seleccionado);
    if (!vuelo || vuelo.estado !== 'listo') return;

    const { minLat, minLng, maxLat, maxLng } = vuelo.limites;
    const capa = L.tileLayer(plantillaDeMosaicos(vuelo.id), {
      minZoom: vuelo.minZoom,
      maxZoom: 21,
      // Arriba del zoom que se cortó, Leaflet estira el último nivel en vez
      // de pedir mosaicos que no existen. Sin esto, acercarse de más deja
      // la pantalla en blanco.
      maxNativeZoom: vuelo.maxZoom,
      bounds: L.latLngBounds([minLat, minLng], [maxLat, maxLng]),
      opacity: opacidad,
      // Los mosaicos de los bordes no existen (la franja volada no llena el
      // rectángulo) y el servidor contesta 204. Sin esto Leaflet dibujaría
      // el ícono de imagen rota sobre el mapa.
      errorTileUrl: 'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7',
    }).addTo(mapa);
    capaVueloRef.current = capa;

    mapa.fitBounds(L.latLngBounds([minLat, minLng], [maxLat, maxLng]), { padding: [24, 24] });
  }, [seleccionado, vuelos]);

  useEffect(() => { capaVueloRef.current?.setOpacity(opacidad); }, [opacidad]);


  // ── Sensores encima ───────────────────────────────────────────────
  useEffect(() => {
    const grupo = capaSensoresRef.current;
    if (!grupo) return;
    grupo.clearLayers();
    for (const s of sensors) {
      if (s.lat == null || s.lng == null) continue;
      const color = s.status === 'good' ? '#1D9E75'
        : s.status === 'warn' ? '#EF9F27'
        : s.status === 'offline' ? '#9AA0A6' : '#E24B4A';
      const icono = L.divIcon({
        className: '',
        html: `<div style="width:30px;height:30px;border-radius:50%;background:${color};color:#fff;`
            + `display:flex;align-items:center;justify-content:center;font-size:11px;font-weight:700;`
            + `border:2px solid #fff;box-shadow:0 2px 8px rgba(0,0,0,.45)">${markerLabel(s.type)}</div>`,
      });
      L.marker([Number(s.lat), Number(s.lng)], { icon: icono, title: getName(s.imei, s.name) }).addTo(grupo);
    }
  }, [sensors, getName]);

  // ── Medición ──────────────────────────────────────────────────────
  const minimoPuntos = (tipo: 'distancia' | 'area') => (tipo === 'area' ? 3 : 2);

  const terminarMedicion = useCallback(() => {
    setPuntos(prev => {
      const tipo = herramientaRef.current;
      if (tipo === 'ninguna' || prev.length < minimoPuntos(tipo)) return [];
      capaPreviaRef.current?.clearLayers();
      const vertices = prev.map(p => [p.lat, p.lng] as [number, number]);
      // Se guarda en el servidor y la lista se llena con lo que él
      // devuelve: así el identificador es el real desde el primer momento
      // y borrarla o renombrarla funciona sin recargar.
      crearMedicion(tipo, vertices)
        .then(creada => setMedidas(ms => [creada, ...ms]))
        .catch(() => setErrorMedicion(t('maps.saveMeasureError')));
      return [];
    });
  }, [t]);
  useEffect(() => { terminarRef.current = terminarMedicion; }, [terminarMedicion]);

  // Escape cancela lo que se esté trazando sin guardarlo.
  useEffect(() => {
    const alTeclear = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setPuntos([]);
        setHerramienta('ninguna');
        capaPreviaRef.current?.clearLayers();
      }
      if (e.key === 'Enter') terminarRef.current();
      // Deshacer el último punto sin tirar todo el trazo: en un lindero de
      // veinte vértices, equivocarse en el último no debería costar los
      // otros diecinueve.
      if (e.key === 'Backspace') { e.preventDefault(); setPuntos(prev => prev.slice(0, -1)); }
    };
    window.addEventListener('keydown', alTeclear);
    return () => window.removeEventListener('keydown', alTeclear);
  }, []);

  /**
   * El doble clic del mapa acerca por omisión. Mientras se mide eso pelea
   * con cerrar la figura, así que se apaga sólo durante la medición.
   */
  useEffect(() => {
    const mapa = mapaRef.current;
    if (!mapa) return;
    if (herramienta === 'ninguna') mapa.doubleClickZoom.enable();
    else mapa.doubleClickZoom.disable();
    const contenedor = mapa.getContainer();
    contenedor.style.cursor = herramienta === 'ninguna' ? '' : 'crosshair';
  }, [herramienta]);

  useEffect(() => { puntosRef.current = puntos; }, [puntos]);

  /**
   * La pista que sigue al cursor mientras se mide.
   *
   * Sin ella, saber cómo se cierra una figura es adivinar. Es lo que hacen
   * las herramientas de mapa conocidas y es la diferencia entre una
   * herramienta que se entiende sola y una que hay que explicar.
   */
  useEffect(() => {
    const mapa = mapaRef.current;
    if (!mapa) return;
    if (herramienta === 'ninguna') {
      pistaRef.current?.remove();
      pistaRef.current = null;
      capaPreviaRef.current?.clearLayers();
      return;
    }
    const pista = L.tooltip({ permanent: true, direction: 'right', offset: [14, 0], opacity: 0.92 })
      .setLatLng(mapa.getCenter())
      .setContent(t('maps.hintStart'))
      .addTo(mapa);
    pistaRef.current = pista;
    return () => { pista.remove(); pistaRef.current = null; };
  }, [herramienta, t]);

  /** Dibuja la medición en curso, que cambia con cada clic. */
  useEffect(() => {
    const capa = capaEnCursoRef.current;
    if (!capa) return;
    capa.clearLayers();
    if (puntos.length === 0) return;

    const esArea = herramienta === 'area';
    if (puntos.length >= 2) {
      const figura = esArea
        ? L.polygon(puntos, { color: '#f59e0b', weight: 2, dashArray: '6,4', fillOpacity: 0.18 })
        : L.polyline(puntos, { color: '#f59e0b', weight: 3, dashArray: '6,4' });
      figura.addTo(capa);
    }
    // Los vértices se dibujan siempre, desde el primero: así se ve que el
    // clic registró aunque todavía no haya línea que mostrar.
    for (const p of puntos) {
      L.circleMarker(p, { radius: 4, color: '#fff', weight: 2, fillColor: '#f59e0b', fillOpacity: 1 }).addTo(capa);
    }
  }, [puntos, herramienta]);

  /** Dibuja las mediciones terminadas, con su valor pegado. */
  useEffect(() => {
    const capa = capaMedidasRef.current;
    if (!capa) return;
    capa.clearLayers();
    for (const m of medidas) {
      const esArea = m.tipo === 'area';
      const pts = m.puntos.map(([lat, lng]) => L.latLng(lat, lng));
      const figura = esArea
        ? L.polygon(pts, { color: '#1D9E75', weight: 2, fillOpacity: 0.16 })
        : L.polyline(pts, { color: '#1D9E75', weight: 3 });
      const valor = esArea
        ? formatearArea(areaGeodesica(pts), i18n.language)
        : formatearDistancia(distanciaTotal(pts), i18n.language);
      figura.bindTooltip(m.nombre ? `${m.nombre} · ${valor}` : valor,
        { permanent: true, direction: 'center' });
      figura.addTo(capa);
      for (const p of pts) {
        L.circleMarker(p, { radius: 3.5, color: '#fff', weight: 2, fillColor: '#1D9E75', fillOpacity: 1 }).addTo(capa);
      }
    }
  }, [medidas, i18n.language]);

  const guardarNombre = async (m: Medicion) => {
    const nombre = borrNombreMedida.trim();
    setRenombrando(null);
    if (nombre === (m.nombre ?? '')) return;
    try {
      await renombrarMedicion(m.id, nombre);
      setMedidas(ms => ms.map(x => (x.id === m.id ? { ...x, nombre: nombre || null } : x)));
    } catch {
      setErrorMedicion(t('maps.saveMeasureError'));
    }
  };

  const quitarMedida = async (m: Medicion) => {
    // Se quita de la pantalla al confirmar el servidor y no antes: si el
    // borrado falla, la medición sigue existiendo y esconderla sería mentir.
    try {
      await borrarMedicion(m.id);
      setMedidas(ms => ms.filter(x => x.id !== m.id));
    } catch {
      setErrorMedicion(t('maps.deleteMeasureError'));
    }
  };

  const borrarTodas = async () => {
    if (!confirm(t('maps.confirmClearAll', { count: medidas.length }))) return;
    const restantes: Medicion[] = [];
    for (const m of medidas) {
      try { await borrarMedicion(m.id); } catch { restantes.push(m); }
    }
    setMedidas(restantes);
    if (restantes.length > 0) setErrorMedicion(t('maps.deleteMeasureError'));
  };

  // ── Subida ────────────────────────────────────────────────────────
  const alElegirArchivo = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const archivo = e.target.files?.[0];
    e.target.value = '';
    if (!archivo) return;
    if (!archivo.name.toLowerCase().endsWith('.zip')) {
      setErrorSubida(t('maps.notZip'));
      return;
    }
    setErrorSubida(null);
    setSubiendo(0);
    try {
      await subirVuelo(archivo, pct => setSubiendo(pct));
      // El servidor contesta en cuanto recibió el archivo; la extracción
      // sigue de su lado. La lista se refresca sola hasta que quede listo.
      await cargar();
    } catch (err: any) {
      // El mensaje genérico escondió un 413 de nginx la primera vez que
      // esto se usó: la subida moría por el límite de tamaño del proxy y la
      // pantalla sólo decía "no se pudo". Un error que no dice qué pasó
      // cuesta más que no tener el error.
      // El error de subida dice en qué paso murió; sin eso, un "error 502"
      // obliga a ir a los logs del servidor para saber si fue la
      // transferencia o el cierre, que son problemas muy distintos.
      const paso = err?.paso as 'abrir' | 'trozo' | 'finalizar' | undefined;
      const status = err?.status ?? err?.response?.status ?? null;
      const detalle = err?.detalle ?? err?.response?.data?.error ?? null;

      const donde = paso === 'trozo' && err?.trozo
        ? t('maps.stepChunk', { indice: err.trozo.indice, total: err.trozo.total })
        : paso === 'finalizar' ? t('maps.stepFinish')
        : paso === 'abrir' ? t('maps.stepOpen')
        : null;

      const porque = detalle
        ?? (status === 413 ? t('maps.tooLarge')
          : status === 507 ? t('maps.noSpace')
          : status ? t('maps.failedCode', { status })
          : t('maps.noConnection'));

      setErrorSubida(donde ? `${donde} — ${porque}` : porque);
    } finally {
      setSubiendo(null);
    }
  };

  const abrirEdicion = (v: Vuelo) => {
    setEditando(v.id);
    setBorrNombre(v.nombre);
    setBorrFecha(v.fecha);
    setErrorEdicion(null);
  };

  const guardarEdicion = async (v: Vuelo) => {
    const nombre = borrNombre.trim();
    if (!nombre) { setErrorEdicion(t('maps.nameRequired')); return; }
    try {
      await editarVuelo(v.id, { nombre, fecha: borrFecha });
      setEditando(null);
      await cargar();
    } catch (err: any) {
      setErrorEdicion(err?.response?.data?.error ?? t('maps.editError'));
    }
  };

  const borrar = async (v: Vuelo) => {
    if (!confirm(t('maps.confirmDelete', {
      name: v.nombre, date: fechaLegible(v.fecha, i18n.language), count: v.mosaicos.toLocaleString(),
    }))) return;
    try {
      await borrarVuelo(v.id);
      if (seleccionado === v.id) setSeleccionado(null);
      await cargar();
    } catch {
      setError(t('maps.deleteError'));
    }
  };

  return (
    <div style={{ display: 'flex', height: '100%', overflow: 'hidden' }}>

      {/* ── Línea de tiempo ───────────────────────────────────────── */}
      <aside style={{
        width: 272, flexShrink: 0, borderRight: '1px solid var(--border)',
        background: 'var(--white)', display: 'flex', flexDirection: 'column', overflow: 'hidden',
      }}>
        <div style={{ padding: '20px 18px 14px', borderBottom: '1px solid var(--border)' }}>
          <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--dark)' }}>{t('maps.title')}</div>
          <div style={{ fontSize: 12, color: 'var(--gray)', marginTop: 4, lineHeight: 1.5 }}>
            {cargando ? t('maps.loading')
              : vuelos.length === 0 ? t('maps.empty')
              : t('maps.count', { count: vuelos.length })}
          </div>
        </div>

        {puedeSubir && (
          <div style={{ padding: '12px 14px', borderBottom: '1px solid var(--border)' }}>
            <input ref={archivoRef} type="file" accept=".zip" onChange={alElegirArchivo} style={{ display: 'none' }} />
            <button
              onClick={() => archivoRef.current?.click()}
              disabled={subiendo !== null}
              style={{
                width: '100%', padding: '9px', borderRadius: 8, border: 'none',
                background: subiendo !== null ? 'var(--border)' : 'var(--green)',
                color: subiendo !== null ? 'var(--gray)' : '#fff',
                fontSize: 12.5, fontWeight: 600, fontFamily: 'inherit',
                cursor: subiendo !== null ? 'default' : 'pointer',
              }}
            >
              {subiendo !== null ? t('maps.uploading', { pct: subiendo }) : t('maps.upload')}
            </button>
            {subiendo !== null && (
              <div style={{ height: 4, background: 'var(--border)', borderRadius: 2, marginTop: 8, overflow: 'hidden' }}>
                <div style={{ width: `${subiendo}%`, height: '100%', background: 'var(--green)', transition: 'width .2s' }} />
              </div>
            )}
            {errorSubida && (
              <div style={{ fontSize: 11, color: 'var(--red)', marginTop: 8, lineHeight: 1.45 }}>{errorSubida}</div>
            )}
            {subiendo === null && !errorSubida && (
              <div style={{ fontSize: 10.5, color: 'var(--gray)', marginTop: 8, lineHeight: 1.45 }}>
                {t('maps.uploadHint')}
              </div>
            )}
          </div>
        )}

        <div style={{ flex: 1, overflowY: 'auto', padding: '10px' }}>
          {error && <div style={{ fontSize: 12, color: 'var(--red)', padding: '8px 4px' }}>{error}</div>}

          {!cargando && vuelos.length === 0 && !error && (
            <div style={{ fontSize: 12, color: 'var(--gray)', padding: '14px 6px', lineHeight: 1.6 }}>
              {t('maps.emptyHelp')}
            </div>
          )}

          {vuelos.map(v => {
            const activo = v.id === seleccionado;
            const listo = v.estado === 'listo';
            return (
              <div
                key={v.id}
                onClick={() => listo && setSeleccionado(v.id)}
                style={{
                  padding: '11px 13px', borderRadius: 10, marginBottom: 6,
                  border: `1.5px solid ${activo ? 'var(--green)' : 'var(--border)'}`,
                  background: activo ? 'var(--green-bg)' : 'var(--bg)',
                  cursor: listo ? 'pointer' : 'default',
                  opacity: listo ? 1 : 0.65,
                }}
              >
                {editando === v.id ? (
                  /* Edición en línea: la fecha de captura viene del script de
                     corte, donde es un argumento que se teclea, así que es
                     fácil que quede la del día de la subida. Corregirla aquí
                     evita tener que borrar y volver a subir cientos de megas. */
                  <div onClick={e => e.stopPropagation()} style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
                    <div>
                      <div style={{ fontSize: 9.5, color: 'var(--gray)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 3 }}>
                        {t('maps.nameLabel')}
                      </div>
                      <input
                        value={borrNombre}
                        onChange={e => setBorrNombre(e.target.value)}
                        autoFocus
                        style={{ width: '100%', fontSize: 12, padding: '5px 8px', borderRadius: 6, border: '1.5px solid var(--border)', background: 'var(--white)', color: 'var(--dark)', fontFamily: 'inherit' }}
                      />
                    </div>
                    <div>
                      <div style={{ fontSize: 9.5, color: 'var(--gray)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 3 }}>
                        {t('maps.dateLabel')}
                      </div>
                      <input
                        type="date"
                        value={borrFecha}
                        onChange={e => setBorrFecha(e.target.value)}
                        style={{ width: '100%', fontSize: 12, padding: '5px 8px', borderRadius: 6, border: '1.5px solid var(--border)', background: 'var(--white)', color: 'var(--dark)', fontFamily: 'inherit' }}
                      />
                    </div>
                    {errorEdicion && <div style={{ fontSize: 10.5, color: 'var(--red)', lineHeight: 1.4 }}>{errorEdicion}</div>}
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button
                        onClick={() => guardarEdicion(v)}
                        style={{ flex: 1, fontSize: 11, fontWeight: 600, padding: '5px', borderRadius: 6, border: 'none', background: 'var(--green)', color: '#fff', cursor: 'pointer', fontFamily: 'inherit' }}
                      >{t('maps.save')}</button>
                      <button
                        onClick={() => { setEditando(null); setErrorEdicion(null); }}
                        style={{ flex: 1, fontSize: 11, padding: '5px', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--white)', color: 'var(--gray)', cursor: 'pointer', fontFamily: 'inherit' }}
                      >{t('maps.cancel')}</button>
                    </div>

                    {/* Borrar vive AQUÍ y no en la tarjeta.
                        Estaba a un centímetro del lápiz de editar, y borra
                        34,000 mosaicos sin vuelta atrás. Una acción que no se
                        puede deshacer no debe estar al alcance del dedo que
                        iba a otro botón: hay que entrar a editar para verla. */}
                    <div style={{ borderTop: '1px solid var(--border)', marginTop: 3, paddingTop: 8 }}>
                      <button
                        onClick={() => borrar(v)}
                        style={{ width: '100%', fontSize: 11, padding: '5px', borderRadius: 6, border: '1px solid var(--red)', background: 'transparent', color: 'var(--red)', cursor: 'pointer', fontFamily: 'inherit' }}
                      >{t('maps.deleteWhole')}</button>
                    </div>
                  </div>
                ) : (
                  <>
                    <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 8 }}>
                      <span style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--dark)' }}>
                        {fechaLegible(v.fecha, i18n.language)}
                      </span>
                      {puedeSubir && (
                        <button
                          onClick={e => { e.stopPropagation(); abrirEdicion(v); }}
                          title={t('maps.editTitle')}
                          style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--gray)', fontSize: 11, padding: 0, flexShrink: 0 }}
                        >✎</button>
                      )}
                    </div>
                    <div style={{ fontSize: 11, color: 'var(--gray)', marginTop: 2 }}>{v.nombre}</div>
                  </>
                )}
                {listo ? (
                  <div style={{ fontSize: 10, color: 'var(--gray)', marginTop: 5, fontFamily: 'monospace' }}>
                    {t('maps.tiles', { count: v.mosaicos })} · {pesoLegible(v.bytes)} · zoom {v.maxZoom}
                  </div>
                ) : (
                  <div style={{ fontSize: 10.5, color: v.estado === 'fallido' ? 'var(--red)' : 'var(--gray)', marginTop: 5 }}>
                    {v.estado === 'fallido' ? (v.error ?? t('maps.failed')) : t('maps.processing')}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {seleccionado && (
          <div style={{ padding: '12px 16px', borderTop: '1px solid var(--border)' }}>
            <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--gray)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 7 }}>
              {t('maps.opacity')}
            </div>
            <input
              type="range" min={0} max={100} value={Math.round(opacidad * 100)}
              onChange={e => setOpacidad(Number(e.target.value) / 100)}
              style={{ width: '100%' }}
            />
            <div style={{ fontSize: 10.5, color: 'var(--gray)', marginTop: 5, lineHeight: 1.45 }}>
              {t('maps.opacityHint')}
            </div>
          </div>
        )}
      </aside>

      {/* ── Mapa ──────────────────────────────────────────────────── */}
      <div style={{ flex: 1, position: 'relative' }}>
        <div ref={contenedorRef} style={{ width: '100%', height: '100%' }} />

        {/* Herramientas de medición. Van sobre el mapa, del lado derecho,
            donde la mano ya las busca por costumbre de otras plataformas. */}
        <div style={{
          position: 'absolute', top: 14, right: 14, zIndex: 500,
          display: 'flex', flexDirection: 'column', gap: 1,
          background: 'var(--white)', borderRadius: 10, padding: 4,
          border: '1px solid var(--border)', boxShadow: '0 2px 10px rgba(0,0,0,0.15)',
        }}>
          {([
            ['ninguna', '↖', t('maps.select')],
            ['distancia', '📏', t('maps.measureDistance')],
            ['area', '⬡', t('maps.measureArea')],
          ] as const).map(([clave, icono, titulo]) => (
            <button
              key={clave}
              onClick={() => { setPuntos([]); setHerramienta(clave); }}
              title={titulo}
              style={{
                width: 34, height: 34, borderRadius: 7, border: 'none', cursor: 'pointer',
                background: herramienta === clave ? 'var(--green)' : 'transparent',
                color: herramienta === clave ? '#fff' : 'var(--gray)',
                fontSize: 15, lineHeight: 1, fontFamily: 'inherit',
              }}
            >{icono}</button>
          ))}
        </div>

        {/* Valor en vivo de lo que se está midiendo. Sin esto habría que
            cerrar la figura para saber si vas por buen camino. */}
        {herramienta !== 'ninguna' && (
          <div style={{
            position: 'absolute', top: 14, right: 60, zIndex: 500,
            background: 'var(--white)', border: '1px solid var(--border)',
            borderRadius: 10, padding: '10px 13px', minWidth: 172,
            boxShadow: '0 2px 10px rgba(0,0,0,0.15)',
          }}>
            {puntos.length >= minimoPuntos(herramienta) ? (
              <>
                <div style={{ fontSize: 17, fontWeight: 600, color: 'var(--dark)', letterSpacing: '-0.01em' }}>
                  {herramienta === 'area'
                    ? formatearArea(areaGeodesica(puntos), i18n.language)
                    : formatearDistancia(distanciaTotal(puntos), i18n.language)}
                </div>
                {herramienta === 'area' && (
                  <div style={{ fontSize: 11, color: 'var(--gray)', marginTop: 2 }}>
                    {t('maps.perimeter')}: {formatearDistancia(perimetro(puntos), i18n.language)}
                  </div>
                )}
                <div style={{ fontSize: 10.5, color: 'var(--gray)', marginTop: 6, lineHeight: 1.4 }}>
                  {t('maps.finishHint')}
                </div>
              </>
            ) : (
              <div style={{ fontSize: 11.5, color: 'var(--gray)', lineHeight: 1.5 }}>
                {herramienta === 'area' ? t('maps.areaHint') : t('maps.distanceHint')}
              </div>
            )}
          </div>
        )}

        {/* Mediciones terminadas */}
        {medidas.length > 0 && (
          <div style={{
            position: 'absolute', top: 132, right: 14, zIndex: 500, width: 186,
            background: 'var(--white)', border: '1px solid var(--border)',
            borderRadius: 10, padding: '9px 11px', maxHeight: '55%', overflowY: 'auto',
            boxShadow: '0 2px 10px rgba(0,0,0,0.15)',
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 7 }}>
              <span style={{ fontSize: 9.5, fontWeight: 700, color: 'var(--gray)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                {t('maps.measurements')}
              </span>
              <button
                onClick={borrarTodas}
                title={t('maps.clearAll')}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--gray)', fontSize: 11, padding: 0 }}
              >🗑</button>
            </div>
            {medidas.map(m => {
              const pts = m.puntos.map(([lat, lng]) => L.latLng(lat, lng));
              return (
                <div key={m.id} style={{ display: 'flex', alignItems: 'flex-start', gap: 6, padding: '5px 0', borderTop: '1px solid var(--border)' }}>
                  <span style={{ fontSize: 11, flexShrink: 0, marginTop: 1 }}>{m.tipo === 'area' ? '⬡' : '📏'}</span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    {renombrando === m.id ? (
                      <input
                        value={borrNombreMedida}
                        onChange={e => setBorrNombreMedida(e.target.value)}
                        onKeyDown={e => { if (e.key === 'Enter') guardarNombre(m); if (e.key === 'Escape') setRenombrando(null); }}
                        onBlur={() => guardarNombre(m)}
                        placeholder={t('maps.namePlaceholder')}
                        autoFocus
                        style={{ width: '100%', fontSize: 11.5, padding: '3px 6px', borderRadius: 5, border: '1.5px solid var(--green)', background: 'var(--white)', color: 'var(--dark)', fontFamily: 'inherit' }}
                      />
                    ) : (
                      <>
                        {m.nombre && (
                          <div style={{ fontSize: 11, color: 'var(--dark)', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {m.nombre}
                          </div>
                        )}
                        <div style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--dark)' }}>
                          {m.tipo === 'area'
                            ? formatearArea(areaGeodesica(pts), i18n.language)
                            : formatearDistancia(distanciaTotal(pts), i18n.language)}
                        </div>
                        {m.tipo === 'area' && (
                          <div style={{ fontSize: 10, color: 'var(--gray)' }}>
                            {formatearDistancia(perimetro(pts), i18n.language)}
                          </div>
                        )}
                      </>
                    )}
                  </div>
                  <span style={{ display: 'flex', gap: 5, flexShrink: 0 }}>
                    <button
                      onClick={() => { setRenombrando(m.id); setBorrNombreMedida(m.nombre ?? ''); }}
                      title={t('maps.rename')}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--gray)', fontSize: 10, padding: 0 }}
                    >✎</button>
                    <button
                      onClick={() => quitarMedida(m)}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--gray)', fontSize: 12, padding: 0 }}
                    >×</button>
                  </span>
                </div>
              );
            })}
            {errorMedicion && (
              <div style={{ fontSize: 10, color: 'var(--red)', marginTop: 6, lineHeight: 1.4 }}>{errorMedicion}</div>
            )}
            <div style={{ fontSize: 9.5, color: 'var(--gray)', marginTop: 7, lineHeight: 1.45, borderTop: '1px solid var(--border)', paddingTop: 6 }}>
              {t('maps.flatNote')}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
